import React, { useEffect, useState } from 'react'
import { observer } from 'mobx-react'
import {
  PositionApplicationStepTitleText,
  PositionApplicationMainBtn,
  PositionApplicationFormContainer,
  PositionApplicationStepSubText,
  PositionApplicationFormContentContainer,
  PositionApplicationFormScrollContainer,
} from 'containers/positions/application/styles'
import { Column, Row } from 'components/common/layout'
import { PositionApplicationStore } from 'stores/position-application'
import { useDebounce, useIsDesktop } from 'utils/hooks'
import styled from 'styled-components'
import { MediumText, SemiBoldText } from 'components/common/text'
import { useScoutMyRequest } from 'apis/hooks'
import { FileUploader } from 'components/scouts/profile/common/file-uploader'
import { TbLink } from 'react-icons/tb'
import { TextInput } from 'components/common/input'
import toast from 'react-hot-toast'
import { TriggerExceptTarget } from 'components/misc/trigger'
import { Checker } from 'components/common/icon'
import { getProfileCompleteStatus, getQueryUrl } from 'utils/formatters'
import { RoutePaths, RouterQueryKey } from 'types/router'
import { useRouter } from 'next/router'

export const PositionApplicationEssentialInfoStep: React.FC<{
  store: PositionApplicationStore
}> = observer(({ store }) => {
  const { data: profile } = useScoutMyRequest()
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const router = useRouter()
  const isDesktop = useIsDesktop()
  const saveLinks = useDebounce(1000, async (links: string[]) => {
    await store.saveTempWithErrorHandling({ links })
    setIsLoading(false)
  })
  const {
    isAbleToApply,
    isCompletedCondition,
    isCompletedEducation,
    isCompletedCareer,
  } = getProfileCompleteStatus(profile)

  const clickNext = async () => {
    if (isLoading)
      return toast.loading('임시 저장 중이에요. 잠시만 기다려주세요.', {
        duration: 2000,
      })
    if (new Set(store.validLinks).size !== store.validLinks.length)
      return alert('중복된 링크가 있어요.')
    store.moveNextStep()
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => store.setState('curTab', 0), [])

  return (
    <PositionApplicationFormContainer>
      <PositionApplicationFormContentContainer>
        <Column style={{ gap: 4 }}>
          <PositionApplicationStepTitleText>
            직무 역량이 드러나는 정보를 추가해 주세요
          </PositionApplicationStepTitleText>
          <PositionApplicationStepSubText>
            프로필 또는 첨부 자료 중 하나는 반드시 포함해야 해요
          </PositionApplicationStepSubText>
        </Column>
        <PositionApplicationFormScrollContainer style={{ gap: 8 }}>
          <DropdownBox>
            <BoxHeader
              style={isAbleToApply ? { cursor: 'default' } : undefined}
              onClick={() => {
                if (isAbleToApply)
                  return alert(
                    '그룹바이 프로필이 있으면 항상 내 정보에 포함돼요.',
                  )
                const msg = profile
                  ? '프로필 필수 항목을 채워야 해요. 지금 바로 프로필을 완성하시겠어요?'
                  : '아직 프로필이 없어요. 지금 바로 작성하시겠어요?'
                if (!confirm(msg)) return
                const pathname =
                  !isCompletedCondition &&
                  !isCompletedEducation &&
                  !isCompletedCareer
                    ? RoutePaths.REQUEST_FORM_JOB_SEARCH_STATUS
                    : !isCompletedEducation
                    ? RoutePaths.REQUEST_FORM_EDUCATION
                    : RoutePaths.REQUEST_FORM_CAREER
                const isUpdatingProfile =
                  isCompletedCondition &&
                  (!isCompletedEducation || !isCompletedCareer)
                const url = getQueryUrl({
                  pathname,
                  query: {
                    [RouterQueryKey.IS_FROM_APPLICATION]: 'true',
                    [RouterQueryKey.FROM_SITE]: router.asPath.split('?')[0],
                    [RouterQueryKey.POSITION_ID]: String(
                      store.positionDetail?.id,
                    ),
                    [RouterQueryKey.STARTUP_ID]: String(
                      store.startupDetail?.id,
                    ),
                    [RouterQueryKey.IS_UPDATING_PROFILE]:
                      isUpdatingProfile.toString(),
                  },
                })
                window.open(url, '_blank') // 새탭으로 열어야 해서 예외적으로 window.open 사용
              }}
            >
              <Checker isChecked={isAbleToApply} />
              <BoxHeaderText>그룹바이 프로필</BoxHeaderText>
            </BoxHeader>
          </DropdownBox>
          <TriggerExceptTarget onTrigger={() => setIsDropdownOpen(false)}>
            <DropdownBox isFocused={isDropdownOpen}>
              <BoxHeader onClick={() => setIsDropdownOpen((prev) => !prev)}>
                <Checker isChecked={store.hasAttachment} />
                <BoxHeaderText>첨부 자료</BoxHeaderText>
              </BoxHeader>
              {isDropdownOpen && (
                <Column style={{ gap: 16, padding: '8px 20px 20px' }}>
                  <Column style={{ gap: 8 }}>
                    <DropdownSectionTitle>링크</DropdownSectionTitle>
                    {store.links.map((link, index) => {
                      return (
                        <Row key={index} style={{ gap: 16 }}>
                          <LinkIconWrapper>
                            <TbLink size={20} />
                          </LinkIconWrapper>
                          <TextInput
                            value={link}
                            onChange={(e) => {
                              store.setLink(e.target.value, index)
                              setIsLoading(true)
                              saveLinks(store.validLinks)
                            }}
                            placeholder={
                              isDesktop
                                ? '블로그, 이력서, 포트폴리오 등의 주소를 입력해주세요.'
                                : '링크 입력'
                            }
                          />
                        </Row>
                      )
                    })}
                  </Column>
                  <Column style={{ gap: 16 }}>
                    <DropdownSectionTitle>파일</DropdownSectionTitle>
                    <FileUploader
                      files={store.files || []}
                      onChange={(file) => {
                        store.addFile(file)
                        store.saveTempWithErrorHandling({
                          files: store.fileIds,
                        })
                      }}
                      onRemove={(id) => {
                        store.removeFile(id)
                        store.saveTempWithErrorHandling({
                          files: store.fileIds,
                        })
                      }}
                    />
                  </Column>
                </Column>
              )}
            </DropdownBox>
          </TriggerExceptTarget>
        </PositionApplicationFormScrollContainer>
      </PositionApplicationFormContentContainer>
      <PositionApplicationMainBtn onClick={clickNext}>
        다음
      </PositionApplicationMainBtn>
    </PositionApplicationFormContainer>
  )
})

const DropdownBox = styled(Column)<{ isFocused?: boolean }>`
  border: 1px solid
    ${(p) => (p.isFocused ? p.theme.color.primary : p.theme.color.line)};
  border-radius: 8px;
`

const BoxHeader = styled(Row)`
  padding: 20px;
  gap: 16px;
  cursor: pointer;
`

const BoxHeaderText = styled(SemiBoldText)`
  color: ${(p) => p.theme.color.black2};
`

const DropdownSectionTitle = styled(MediumText)`
  color: ${(p) => p.theme.color.gray1};
`

const LinkIconWrapper = styled(Row)`
  width: 30px;
  height: 30px;
  flex: none;
  justify-content: center;
  border-radius: 100px;
  background-color: ${(p) => p.theme.color.background2};
`
